'use client'

// #region Imports
//App
import NextLink from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
// Components
import {
  Box,
  CloseButton,
  Drawer,
  Flex,
  Group,
  Heading,
  HStack,
  IconButton,
  Link,
  List,
  Portal,
  Separator,
  Show,
  Span,
  useBreakpointValue,
} from '@chakra-ui/react'
import { ColorModeButton } from '@repo/ui/components/color-mode'
import ChakraImage from '@repo/ui/components/custom/chakra-image'
// Data
import { bottomNavigation, productMenu, topNavigation } from '@/lib/config/navigation'
import { domainName } from '@/lib/config/site'
// Icons
import { faBars, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
// #endregion

// Component begins ------>
export default function Header() {
  const pathname = usePathname()
  const isMobile = useBreakpointValue({ base: true, sm: true, md: true, lg: false })
  const [open, setOpen] = useState(false)
  const [isLoading, setLoading] = useState(true)

  // check if the window is loaded and disable the button loading.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  return (
    <Flex position='relative'>
      <Flex
        zIndex='40'
        marginX='auto'
        justifyContent='space-between'
        paddingTop={{ base: '2', sm: '2', md: '2', lg: '0' }}
        paddingX={{ base: '8', sm: '8', md: '8', lg: '12' }}
        height='60px'
        backgroundColor={{ base: 'blackAlpha.600', sm: 'blackAlpha.600', md: 'blackAlpha.600', lg: 'transparent' }}
        position='fixed'
        width='full'>
        <Heading size='md' as='h1' marginY='auto'>
          <NextLink href='/'>
            <ChakraImage
              src='/images/banner/NIC_Wordmark_Primary_White.svg'
              alt='NIC Industries'
              height={10}
              width='auto'
              borderRadius='0'
              disableSkeleton={true}
            />
          </NextLink>
        </Heading>
        <Show when={!isLoading && !isMobile}>
          <Group gap='6' justifyContent='center' alignItems='center'>
            {topNavigation.map((item: { href: string; name: string }, index: number) => (
              <Link key={`nav-item-${index}`} display='flex' fontWeight='500' color='white' asChild>
                <NextLink href={item.href}>{item.name}</NextLink>
              </Link>
            ))}
            <ColorModeButton color='white' backgroundColor='transparent' restrictToLightDark={true} />
          </Group>
        </Show>
        {/* 👇 mobile nav */}
        <Show when={isMobile || false}>
          <Drawer.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
            <Drawer.Trigger asChild>
              <IconButton variant='plain' zIndex='40'>
                <Span className='sr-only'>Open main menu</Span>
                <FontAwesomeIcon size='lg' color='white' icon={faBars} />
              </IconButton>
            </Drawer.Trigger>
            <Portal>
              <Drawer.Backdrop />
              <Drawer.Positioner>
                <Drawer.Content>
                  <Drawer.Header borderBottomWidth='1px' fontWeight='bold'>
                    <HStack>
                      <ColorModeButton restrictToLightDark={true} />
                      <Span className='sr-only'>{domainName}</Span>
                      <Link asChild>
                        <NextLink href={'/'}>{domainName}</NextLink>
                      </Link>
                    </HStack>
                  </Drawer.Header>
                  <Drawer.Body>
                    <Box>
                      <List.Root paddingY='6' spaceY='6' variant='plain'>
                        {bottomNavigation.map((item: { href: string; name: string }, index: number) => (
                          <List.Item key={`bottom-nav-item-${index}`}>
                            <Link asChild>
                              <NextLink href={item.href}>{item.name}</NextLink>
                            </Link>
                          </List.Item>
                        ))}
                      </List.Root>
                      <Separator />
                      <List.Root paddingY='6' spaceY='6' variant='plain'>
                        {productMenu.map((item: { href: string; name: string; target?: string }, index: number) => (
                          <List.Item key={`product-menu-item-${index}`} alignItems='center'>
                            <List.Indicator asChild>
                              <FontAwesomeIcon size='sm' icon={faUpRightFromSquare} />
                            </List.Indicator>
                            <Link width='full' asChild>
                              <NextLink target={item.target} href={item.href}>
                                {item.name}
                              </NextLink>
                            </Link>
                          </List.Item>
                        ))}
                      </List.Root>
                    </Box>
                  </Drawer.Body>
                  <Drawer.CloseTrigger asChild>
                    <CloseButton size='sm' />
                  </Drawer.CloseTrigger>
                </Drawer.Content>
              </Drawer.Positioner>
            </Portal>
          </Drawer.Root>
        </Show>
      </Flex>
      <Box zIndex='35' height='60px' position='fixed' width='full' top='0' backgroundColor='black'>
        &nbsp;
      </Box>
    </Flex>
  )
}
